import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Button, ReactTable } from "@aix/ui"
import { trpc } from "~/lib/trpc"
import toast from "react-hot-toast"
import CreateCoachModal from "./CreateCoachModal"

interface Coach {
  id: string
  name: string
  icon: string
  type: "pillar" | "side-quest"
  tone: "tough-love" | "chill" | "science-y"
  status: "active" | "paused" | "completed" | "archived"
  startDate: string | null
  targetEndDate: string | null
  actualEndDate: string | null
  createdAt: Date
  updatedAt: Date
}

export default function CoachListTab() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [typeFilter, setTypeFilter] = useState<string>("all")
  const [showDeleted, setShowDeleted] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  // Query coaches
  const {
    data: coachesData,
    isLoading,
    refetch,
  } = trpc.coaches.list.useQuery({
    status: statusFilter === "all" ? undefined : (statusFilter as any),
    type: typeFilter === "all" ? undefined : (typeFilter as any),
    includeDeleted: showDeleted,
  })

  // Restore mutation
  const restoreMutation = trpc.coaches.restore.useMutation()

  // Delete mutation
  const deleteMutation = trpc.coaches.delete.useMutation()

  const coaches = coachesData?.coaches || []

  // Determine if we're on mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768

  // Define mobile columns (fewer columns for mobile)
  const mobileColumns: any[] = [
    {
      Header: "Coach",
      accessor: "name",
      Cell: (info) => {
        const coach = info.row.original
        return (
          <div className="flex items-center gap-2">
            <span className="text-2xl">{coach.icon}</span>
            <div className="flex flex-col">
              <span className="font-semibold text-sm">{coach.name}</span>
              <span className="text-xs text-muted-foreground">
                {coach.type === "pillar" ? "🏛️ Pillar" : "⚔️ Side Quest"} • {coach.status}
              </span>
            </div>
          </div>
        )
      },
    },
    {
      Header: "Actions",
      accessor: "id",
      Cell: (info) => {
        const coach = info.row.original
        const isDeleted = !!coach.deletedAt

        return (
          <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
            {isDeleted ? (
              <Button
                variant="outline"
                size="sm"
                className="border-2 border-primary bg-primary/10 hover:bg-primary/20 text-xs whitespace-nowrap"
                onClick={async (e) => {
                  e.stopPropagation()
                  try {
                    await restoreMutation.mutateAsync({ coachId: info.getValue() })
                    toast.success("Coach restored successfully!")
                    void refetch()
                  } catch (error: any) {
                    toast.error(`Error restoring coach: ${error.message}`)
                  }
                }}
                loading={restoreMutation.isLoading}
              >
                ♻️ Restore
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-2 border-border text-xs whitespace-nowrap"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({
                      to: "/coach-os/coach/$coachId",
                      // @ts-ignore
                      params: { coachId: info.getValue() },
                    })
                  }}
                >
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-2 border-border bg-primary/10 hover:bg-primary/20 text-xs whitespace-nowrap"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({
                      to: "/coach-os/coach/$coachId",
                      // @ts-ignore
                      params: { coachId: info.getValue() },
                      // @ts-ignore
                      search: { tab: "chat" },
                    })
                  }}
                >
                  💬 Chat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground text-xs whitespace-nowrap"
                  onClick={async (e) => {
                    e.stopPropagation()
                    if (!confirm(`Are you sure you want to delete "${coach.name}"?`)) {
                      return
                    }
                    try {
                      await deleteMutation.mutateAsync({ coachId: info.getValue() })
                      toast.success("Coach deleted successfully!")
                      void refetch()
                    } catch (error: any) {
                      toast.error(`Error deleting coach: ${error.message}`)
                    }
                  }}
                  loading={deleteMutation.isLoading}
                >
                  🗑️ Delete
                </Button>
              </>
            )}
          </div>
        )
      },
    },
  ]

  // Define desktop columns (full columns for desktop)
  const desktopColumns: any[] = [
    {
      Header: "Icon",
      accessor: "icon",
      Cell: (info) => <span className="text-2xl">{info.getValue()}</span>,
    },
    {
      Header: "Name",
      accessor: "name",
      Cell: (info) => <span className="font-semibold">{info.getValue()}</span>,
    },
    {
      Header: "Type",
      accessor: "type",
      Cell: (info) => {
        const type = info.getValue()
        return (
          <span
            className={`px-2 py-1 text-xs font-semibold border-2 border-border ${
              type === "pillar" ? "bg-primary text-primary-foreground" : "bg-secondary"
            }`}
          >
            {type === "pillar" ? "🏛️ Pillar" : "⚔️ Side Quest"}
          </span>
        )
      },
    },
    {
      Header: "Tone",
      accessor: "tone",
      Cell: (info) => {
        const tone = info.getValue()
        const toneEmoji = {
          "tough-love": "💪",
          chill: "😎",
          "science-y": "🔬",
        }
        return (
          <span className="text-sm">
            {toneEmoji[tone]} {tone}
          </span>
        )
      },
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: (info) => {
        const status = info.getValue()
        const statusColors = {
          active: "bg-primary text-primary-foreground",
          paused: "bg-secondary",
          completed: "bg-accent",
          archived: "bg-muted",
        }
        return (
          <span className={`px-2 py-1 text-xs font-semibold border-2 border-border ${statusColors[status]}`}>
            {status}
          </span>
        )
      },
    },
    {
      Header: "Target End",
      accessor: "targetEndDate",
      Cell: (info) => {
        const targetEndDate = info.getValue()
        return targetEndDate ? (
          <span className="text-sm text-muted-foreground">{targetEndDate}</span>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        )
      },
    },
    {
      Header: "Actions",
      accessor: "id",
      Cell: (info) => {
        const coach = info.row.original
        const isDeleted = !!coach.deletedAt

        return (
          <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
            {isDeleted ? (
              <Button
                variant="outline"
                size="sm"
                className="border-2 border-primary bg-primary/10 hover:bg-primary/20 text-xs whitespace-nowrap"
                onClick={async (e) => {
                  e.stopPropagation()
                  try {
                    await restoreMutation.mutateAsync({ coachId: info.getValue() })
                    toast.success("Coach restored successfully!")
                    void refetch()
                  } catch (error: any) {
                    toast.error(`Error restoring coach: ${error.message}`)
                  }
                }}
                loading={restoreMutation.isLoading}
              >
                ♻️ Restore
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-2 border-border text-xs whitespace-nowrap"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({
                      to: "/coach-os/coach/$coachId",
                      // @ts-ignore
                      params: { coachId: info.getValue() },
                    })
                  }}
                >
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-2 border-border bg-primary/10 hover:bg-primary/20 text-xs whitespace-nowrap"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({
                      to: "/coach-os/coach/$coachId",
                      // @ts-ignore
                      params: { coachId: info.getValue() },
                      // @ts-ignore
                      search: { tab: "chat" },
                    })
                  }}
                >
                  💬 Chat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground text-xs whitespace-nowrap"
                  onClick={async (e) => {
                    e.stopPropagation()
                    if (!confirm(`Are you sure you want to delete "${coach.name}"?`)) {
                      return
                    }
                    try {
                      await deleteMutation.mutateAsync({ coachId: info.getValue() })
                      toast.success("Coach deleted successfully!")
                      void refetch()
                    } catch (error: any) {
                      toast.error(`Error deleting coach: ${error.message}`)
                    }
                  }}
                  loading={deleteMutation.isLoading}
                >
                  🗑️ Delete
                </Button>
              </>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6">
      {/* Header with filters and create button */}
      <div className="flex flex-col gap-4 bg-secondary border-4 border-border p-3 md:p-4">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 md:gap-4">
          {/* Status Filter */}
          <div className="flex flex-col gap-1 flex-1 min-w-[120px]">
            <label className="text-xs font-semibold text-muted-foreground">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border-2 border-border bg-background px-2 md:px-3 py-2 text-xs md:text-sm"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Type Filter */}
          <div className="flex flex-col gap-1 flex-1 min-w-[120px]">
            <label className="text-xs font-semibold text-muted-foreground">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border-2 border-border bg-background px-2 md:px-3 py-2 text-xs md:text-sm"
            >
              <option value="all">All</option>
              <option value="pillar">Pillar</option>
              <option value="side-quest">Side Quest</option>
            </select>
          </div>

          {/* Show Deleted Toggle */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-muted-foreground">Deleted</label>
            <label className="flex items-center gap-2 border-2 border-border bg-background px-2 md:px-3 py-2 text-xs md:text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="w-4 h-4"
              />
              <span>Show deleted</span>
            </label>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full">
          <Button
            onClick={() => refetch()}
            variant="outline"
            className="border-2 border-border flex-1 sm:flex-initial text-xs md:text-sm"
            loading={isLoading}
          >
            🔄 Refresh
          </Button>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-primary text-primary-foreground border-2 border-border flex-1 sm:flex-initial text-xs md:text-sm"
          >
            ➕ Create Coach
          </Button>
        </div>
      </div>

      {/* Coaches Table */}
      <div className="bg-background border-4 border-border overflow-x-auto">
        {isLoading ? (
          <div className="p-4 md:p-8 text-center text-muted-foreground text-sm md:text-base">Loading coaches...</div>
        ) : coaches.length === 0 ? (
          <div className="p-4 md:p-8 text-center">
            <p className="text-muted-foreground mb-4 text-sm md:text-base">No coaches found</p>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-primary text-primary-foreground text-xs md:text-sm"
            >
              Create Your First Coach
            </Button>
          </div>
        ) : (
          <div className="md:min-w-[640px]">
            <ReactTable
              data={coaches}
              columns={isMobile ? mobileColumns : desktopColumns}
              onRowClick={(coach) => {
                // Only navigate on row click for desktop
                if (!isMobile) {
                  navigate({
                    to: "/coach-os/coach/$coachId",
                    // @ts-ignore
                    params: { coachId: coach.id },
                  })
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Create Coach Modal */}
      <CreateCoachModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          void refetch()
          toast.success("Coach created successfully!")
        }}
      />
    </div>
  )
}
